import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heart, DollarSign, CreditCard, CheckCircle, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSearchParams } from "react-router-dom";
const donationAmounts = [25, 50, 100, 250, 500, 1000];

const Donate = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(100);
  const [customAmount, setCustomAmount] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    employer: "",
    occupation: "",
  });

  // Handle success/cancel redirects from Stripe
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast({
        title: "Thank you for your donation!",
        description: "Your contribution will help elect Matt Bucher.",
      });
    } else if (searchParams.get("canceled") === "true") {
      toast({
        title: "Donation canceled",
        description: "Your donation was not processed.",
        variant: "destructive",
      });
    }
  }, [searchParams, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;

    if (!amount || amount < 1) {
      toast({
        title: "Invalid amount",
        description: "Please select or enter a valid donation amount.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, donorInfo: formData }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || `Checkout failed (${res.status})`);
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Error",
        description: "Failed to process donation. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(null);
  };

  const currentAmount = customAmount ? parseFloat(customAmount) : selectedAmount;

  return (
    <>
      <Helmet>
        <title>Donate | Matt Bucher for State Representative</title>
        <meta
          name="description"
          content="Support Matt Bucher's campaign for State Representative. Your contribution helps bring conservative leadership to the 33rd District."
        />
        <link rel="canonical" href="https://votebucher.com/donate" />
        <meta property="og:url" content="https://votebucher.com/donate" />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Header />

        <main id="main-content" className="flex-grow">
          {/* Hero Banner */}
          <section className="bg-primary py-20" aria-labelledby="donate-heading">
            <div className="container mx-auto px-4 text-center">
              <Heart className="h-16 w-16 mx-auto mb-6 text-primary-foreground" aria-hidden="true" />
              <h1 id="donate-heading" className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4">
                Support the Campaign
              </h1>
              <p className="font-body text-xl text-primary-foreground/90 max-w-2xl mx-auto">
                Your contribution helps Matt Bucher fight for conservative values in the 33rd District.
              </p>
            </div>
          </section>

          {/* Why Donate */}
          <section className="py-12 bg-background" aria-label="Benefits of donating">
            <div className="container mx-auto px-4">
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" role="list">
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h2 className="font-display font-bold text-foreground">Fuel the Fight</h2>
                    <p className="font-body text-sm text-muted-foreground">
                      Your donation funds campaign outreach and voter contact.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h2 className="font-display font-bold text-foreground">Every Dollar Counts</h2>
                    <p className="font-body text-sm text-muted-foreground">
                      We stretch every contribution to maximize impact.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h2 className="font-display font-bold text-foreground">Secure & Compliant</h2>
                    <p className="font-body text-sm text-muted-foreground">
                      All donations are processed securely via Stripe.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Donation Form */}
          <section className="py-16 bg-card" aria-label="Donation form">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl mx-auto">
                <form onSubmit={handleSubmit} className="space-y-8" aria-label="Make a donation">
                  {/* Amount Selection */}
                  <fieldset>
                    <legend className="font-display text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <DollarSign className="h-6 w-6 text-primary" aria-hidden="true" />
                      Select Amount
                    </legend>
                    <div className="grid grid-cols-3 gap-3 mb-4" role="group" aria-label="Preset donation amounts">
                      {donationAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => handleAmountSelect(amount)}
                          aria-pressed={selectedAmount === amount}
                          className={`py-4 px-2 rounded-lg font-body font-bold text-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                            selectedAmount === amount
                              ? "bg-primary text-primary-foreground shadow-lg"
                              : "bg-background border-2 border-border text-foreground hover:border-primary"
                          }`}
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>
                    <div>
                      <Label htmlFor="customAmount" className="font-body font-semibold">
                        Or enter a custom amount:
                      </Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="customAmount"
                          type="number"
                          min="1"
                          value={customAmount}
                          onChange={(e) => handleCustomAmountChange(e.target.value)}
                          className="pl-8"
                          placeholder="Other amount"
                        />
                      </div>
                    </div>
                  </fieldset>

                  {/* Personal Information */}
                  <fieldset>
                    <legend className="font-display text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <CreditCard className="h-6 w-6 text-primary" aria-hidden="true" />
                      Your Information
                    </legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName" className="font-body font-semibold">
                          First Name *
                        </Label>
                        <Input
                          id="firstName"
                          required
                          value={formData.firstName}
                          onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="font-body font-semibold">
                          Last Name *
                        </Label>
                        <Input
                          id="lastName"
                          required
                          value={formData.lastName}
                          onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <Label htmlFor="email" className="font-body font-semibold">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        className="mt-1"
                      />
                    </div>

                    <div className="mt-4">
                      <Label htmlFor="address" className="font-body font-semibold">
                        Address *
                      </Label>
                      <Input
                        id="address"
                        required
                        value={formData.address}
                        onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                        className="mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div className="col-span-2">
                        <Label htmlFor="city" className="font-body font-semibold">
                          City *
                        </Label>
                        <Input
                          id="city"
                          required
                          value={formData.city}
                          onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state" className="font-body font-semibold">
                          State *
                        </Label>
                        <Input
                          id="state"
                          required
                          value={formData.state}
                          onChange={(e) => setFormData((prev) => ({ ...prev, state: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="zip" className="font-body font-semibold">
                          ZIP *
                        </Label>
                        <Input
                          id="zip"
                          required
                          value={formData.zip}
                          onChange={(e) => setFormData((prev) => ({ ...prev, zip: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label htmlFor="employer" className="font-body font-semibold">
                          Employer *
                        </Label>
                        <Input
                          id="employer"
                          required
                          value={formData.employer}
                          onChange={(e) => setFormData((prev) => ({ ...prev, employer: e.target.value }))}
                          className="mt-1"
                          placeholder="Or 'Retired' / 'Self-Employed'"
                        />
                      </div>
                      <div>
                        <Label htmlFor="occupation" className="font-body font-semibold">
                          Occupation *
                        </Label>
                        <Input
                          id="occupation"
                          required
                          value={formData.occupation}
                          onChange={(e) => setFormData((prev) => ({ ...prev, occupation: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </fieldset>

                  {/* Legal */}
                  <div className="bg-background p-4 rounded-lg border border-border">
                    <p className="font-body text-sm text-muted-foreground">
                      Contributions to Friends of Matt Bucher are not tax deductible. Contributions may not exceed $600
                      per election cycle.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    variant="cta"
                    size="xl"
                    className="w-full"
                    disabled={isLoading || !currentAmount || currentAmount < 1}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      `Donate $${currentAmount || 0} Now`
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Donate;
